import React from 'react';
import { useHooks } from '@/components/templates/ImportPanel/hooks';
import { RawEntry } from '@/types';

export interface ImportPanelProps {
  entries: RawEntry[];
  onImport: (mergedEntries: RawEntry[], importedDates: string[]) => Promise<void>;
  onClearAll: () => Promise<void>;
}

const ImportPanel: React.FC<ImportPanelProps> = (props) => {
  const {
    inputText,
    fallbackDate,
    error,
    previewData,
    replaceMode,
    setReplaceMode,
    fileInputRef,
    handleTextChange,
    handleDateChange,
    handleFileUpload,
    handleImport,
    handleClearInput,
    handleClearAll
  } = useHooks(props);

  return (
    <div className="space-y-6 animate-fade-in delay-100">
      <div className="glass-panel p-6 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-display font-semibold tracking-tight text-foreground">
              Import Timelog Lines
            </h2>
            <p className="text-xs text-foreground/50 font-mono mt-0.5">
              PASTE CHAT EXPORTS OR DRAG IN FILES
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Fallback Date Picker */}
            <div className="flex flex-col">
              <label className="text-[10px] uppercase font-mono tracking-wider text-foreground/40 mb-1">
                Fallback Date
              </label>
              <input
                type="date"
                value={fallbackDate}
                onChange={handleDateChange}
                className="bg-background border border-card-border rounded px-3 py-1.5 text-sm font-mono text-foreground focus:outline-none focus:border-accent/40"
              />
            </div>
            
            {/* Styled File Upload */}
            <div className="flex flex-col">
              <span className="text-[10px] uppercase font-mono tracking-wider text-foreground/40 mb-1">
                Load Document
              </span>
              <label className="cursor-pointer bg-background hover:bg-foreground/5 border border-card-border hover:border-foreground/20 rounded px-4 py-1.5 text-sm font-mono text-foreground/80 flex items-center justify-center transition-all">
                <span>Upload File</span>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".txt,.log,.csv"
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Textarea */}
        <div className="relative">
          <textarea
            value={inputText}
            onChange={handleTextChange}
            placeholder={`Example input:\n\n2026-06-08\nIN | 09:00 | Alex\nOUT | 12:30 | Alex\nIN | 13:30 | Alex\nOUT | 18:00 | Alex`}
            className="w-full h-64 bg-background/50 border border-card-border focus:border-accent/40 rounded p-4 text-sm font-mono text-foreground focus:outline-none resize-none transition-all placeholder:text-foreground/20"
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs font-mono">
            [ERROR] {error}
          </div>
        )}

        {/* Actions Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
          <div className="flex items-center gap-4">
            <button
              onClick={handleClearAll}
              className="px-4 py-2 text-xs font-mono uppercase tracking-wider text-red-400 border border-red-500/20 hover:bg-red-500/10 rounded transition-all cursor-pointer"
            >
              Wipe Storage
            </button>
            <label className="flex items-center gap-2 cursor-pointer select-none group">
              <input
                type="checkbox"
                checked={replaceMode}
                onChange={(e) => setReplaceMode(e.target.checked)}
                className="accent-accent w-3.5 h-3.5 cursor-pointer"
              />
              <span className={`text-[10px] font-mono uppercase tracking-wider transition-colors ${
                replaceMode ? 'text-accent' : 'text-foreground/40 group-hover:text-foreground/60'
              }`}>
                Replace Mode
              </span>
            </label>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleClearInput}
              disabled={!inputText}
              className="px-4 py-2 text-xs font-mono uppercase tracking-wider text-foreground/60 hover:text-foreground hover:bg-foreground/5 rounded transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              Clear Input
            </button>
            <button
              onClick={handleImport}
              className="px-6 py-2 text-xs font-mono uppercase tracking-wider text-background bg-accent hover:bg-accent/90 border border-accent/20 rounded font-semibold transition-all shadow-md shadow-accent/10 cursor-pointer"
            >
              Execute Import
            </button>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      {previewData && previewData.length > 0 && (
        <div className="glass-panel p-6 space-y-3 border-accent/10">
          <div className="border-b border-card-border pb-2">
            <h3 className="text-sm uppercase font-mono tracking-wider text-accent font-semibold">
              {"// Successful Import Summary"}
            </h3>
            <p className="text-[10px] text-foreground/40 font-mono mt-0.5">
              THE FOLLOWING DAYS WERE UPDATED OR ADDED
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {previewData.map((item) => (
              <div key={item.date} className="flex justify-between items-center p-3 bg-background/30 border border-card-border rounded font-mono text-sm">
                <span className="text-foreground/60">{item.date}</span>
                <span className="font-semibold text-accent num-mono">{item.displayHours}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportPanel;
